import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  type RagChatCustomData,
  RagChatMessageItem,
  type RagChatReferenceItem,
} from '../../../domain/ragChat/types';
import { RagEvaluationFeedBackEnum } from '../../../domain/ragcEvaluation/types';

import {
  RagEvaluationActions,
  ragEvaluationSelector,
} from '../../../redux/ui/dataRagEvaluation';
import { gaClickEvent } from '../../../utils/googleAnalytics';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';
import { parseFootnotes } from './parseFootnotes';

/**
 * 参考ドキュメントのリンクを表示するコンポーネント
 */
const ReferenceLinks = memo(
  ({ references }: { references: RagChatReferenceItem[] }) => {
    if (!references || references.length === 0) return null;

    return (
      <Box>
        <List dense>
          {references.map((ref) => (
            <ListItem
              key={ref.marker}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="body2" sx={{ mr: 1, fontSize: '0.9rem' }}>
                {ref.marker}.
              </Typography>
              <Link
                href={ref.url}
                target="_blank"
                rel="noopener"
                sx={{ fontSize: '0.8rem' }}
                onClick={() => {
                  gaClickEvent('AIアシスタント参照元リンク');
                }}
              >
                {ref.title}
              </Link>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }
);

/**
 * メッセージアイテムコンポーネント
 */
export const MessageItem = memo(
  ({
    role,
    content,
    id,
    isNew = false,
    isLatest = false,
    status,
    customData,
    reference_documents,
    chatHistoryId,
  }: {
    role: RagChatMessageItem['role'];
    content: RagChatMessageItem['content'];
    id: RagChatMessageItem['id'];
    isNew?: boolean;
    isLatest?: boolean;
    status: 'submitted' | 'streaming' | 'ready' | 'error';
    customData: RagChatCustomData | null;
    reference_documents: RagChatReferenceItem[];
    chatHistoryId: string | undefined;
  }) => {
    const dispatch = useDispatch();
    const isUser = role === 'user';
    const messageRef = useRef<HTMLDivElement>(null);

    const ragEvaluation = useSelector(ragEvaluationSelector);

    const onSubmitRagEvaluation = ({
      chatHistoryId,
      feedback,
    }: {
      chatHistoryId: string | undefined;
      feedback: RagEvaluationFeedBackEnum;
    }) => {
      if (!chatHistoryId || chatHistoryId === '') {
        console.error('IDが無効です。');
        return;
      }
      // 評価用のAPIをリクエスト、すでに評価済みの場合はリセットする
      if (getRagEvaluationItem(chatHistoryId)?.feedback === feedback) {
        // 評価済みの場合は評価をリセットする
        dispatch(
          RagEvaluationActions.postRagEvaluationRagAction({
            id: chatHistoryId,
            feedback: '',
          })
        );
        return;
      } else {
        // 評価を送信する
        dispatch(
          RagEvaluationActions.postRagEvaluationRagAction({
            id: chatHistoryId,
            feedback,
          })
        );
      }
    };

    const getRagEvaluationItem = (chatHistoryId: string | undefined) => {
      if (!chatHistoryId) return null;
      const evaluation = ragEvaluation.evaluation.find(
        (evaluation) => evaluation.id === chatHistoryId
      );
      return evaluation;
    };

    const getRagEvaluationStatus = (
      chatHistoryId: string | undefined
    ): RagEvaluationFeedBackEnum => {
      if (!chatHistoryId) return '';
      const evaluation = ragEvaluation.evaluation.find(
        (evaluation) => evaluation.id === chatHistoryId
      );
      return evaluation ? evaluation.feedback : '';
    };

    const isRagEvaluationLoading = (
      chatHistoryId: string | undefined
    ): boolean => {
      if (!chatHistoryId) return false;
      const evaluation = ragEvaluation.evaluation.find(
        (evaluation) => evaluation.id === chatHistoryId
      );
      return evaluation?.loadingStatus === 'loading' ? true : false;
    };

    useEffect(() => {
      if (isNew && messageRef.current) {
        messageRef.current.animate(
          [
            { opacity: 0, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 500 }
        );
      }
    }, [isNew]);

    const [hoveredMarker, setHoveredMarker] = useState<number | null>(null);

    // hover 開始 / 終了ハンドラを生成するユーティリティ
    const hoverHandlers = (marker: number) => ({
      onMouseEnter: () => setHoveredMarker(marker),
      onMouseLeave: () => setHoveredMarker(null),
    });

    const { parseContent, markers } = parseFootnotes(
      content.trim(),
      reference_documents,
      hoverHandlers
    );

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          gap: 1, // アイコンとメッセージの間のスペース
        }}
      >
        {/* AIのメッセージのみアイコンを左側に表示（UI調整が発生する可能性があるため残している） */}
        {/* {!isUser && <img src="/favicon.svg" alt="AI" width={32} height={32} />} */}

        {/* メッセージ本体 */}
        <Box
          ref={messageRef}
          sx={{
            px: 2,
            py: 1,
            maxWidth: '90%',
            backgroundColor: isUser ? 'primary.main' : 'grey.50',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
          >
            {parseContent}
          </Typography>
          {/* 脚注 */}
          {!isUser &&
            (!isLatest || isNew) &&
            markers.length > 0 &&
            reference_documents.length > 0 && (
              <Box mt={2} sx={{}}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    m: 0.5,
                  }}
                >
                  <ManageSearchIcon sx={{ color: '' }} />
                  <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                    参照元
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {markers.map((marker, index) => (
                    <Button
                      key={`marker-${index}`}
                      onClick={() => {
                        const reference = reference_documents.find(
                          (ref) => ref.marker === marker
                        );
                        if (reference?.url) {
                          window.open(reference.url, '_blank', 'noopener');
                        }
                        gaClickEvent('AIアシスタント参照元リンク');
                      }}
                      disabled={
                        !reference_documents.find(
                          (ref) => ref.marker === marker
                        )
                      }
                      sx={{
                        backgroundColor:
                          hoveredMarker === marker
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'white',
                        fontSize: '0.8rem',
                        py: 0.5,
                        mt: 0.5,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        maxWidth: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          justifyContent: 'flex-start',
                          width: '100%',
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '18px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: 'grey.500',
                              color: 'white',
                              marginLeft: '3px',
                            }}
                          >
                            {marker}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                            lineHeight: '1.2rem',
                          }}
                        >
                          {reference_documents.find(
                            (ref) => ref.marker === marker
                          )?.title ?? 'データがありません'}
                        </Box>
                      </Box>
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          {/* 参考ドキュメントリンク */}
          {!isUser &&
            (!isLatest || isNew) &&
            reference_documents.length > 0 && (
              <Box mt={1}>
                <Accordion
                  sx={{
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}
                    aria-controls="reference-links-content"
                    id="reference-links-header"
                    sx={{
                      minHeight: '32px', // 高さを小さく設定
                      '& .MuiAccordionSummary-content': {
                        margin: 0, // コンテンツの余白を削除
                        alignItems: 'center', // 垂直方向に中央揃え
                      },
                      '&.Mui-expanded': {
                        minHeight: '32px',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center', // 水平方向に中央揃え
                        gap: 1, // アイコンとテキストの間のスペース
                        width: '100%', // 必要に応じて幅を指定
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        すべて表示
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 0.5, // パディングを調整
                    }}
                  >
                    <ReferenceLinks references={reference_documents} />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

          {!isUser && (!isLatest || isNew) && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                pt: 1,
              }}
            >
              <Tooltip
                title={
                  getRagEvaluationItem(chatHistoryId)?.feedback === 'good'
                    ? '評価リセット'
                    : 'Good評価'
                }
                arrow
              >
                <span>
                  <IconButton
                    aria-label={`thumb-up-${id}`}
                    onClick={() => {
                      onSubmitRagEvaluation({
                        chatHistoryId: chatHistoryId,
                        feedback: 'good',
                      });
                      getRagEvaluationItem(chatHistoryId)?.feedback === 'good'
                        ? gaClickEvent('AIアシスタント回答評価good', {
                            event_click_state: 'OFF',
                          })
                        : gaClickEvent('AIアシスタント回答評価good', {
                            event_click_state: 'ON',
                          });
                    }}
                    size="small"
                    color={
                      getRagEvaluationStatus(chatHistoryId) === 'good'
                        ? 'primary'
                        : undefined
                    }
                    disabled={isRagEvaluationLoading(chatHistoryId)} //ローディング中はボタンを無効化
                  >
                    <ThumbUpOffAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* 評価Bad */}
              <Tooltip
                title={
                  getRagEvaluationItem(chatHistoryId)?.feedback === 'bad'
                    ? '評価リセット'
                    : 'Bad評価'
                }
                arrow
              >
                <span>
                  <IconButton
                    aria-label={`thumb-down-${id}`}
                    onClick={() => {
                      onSubmitRagEvaluation({
                        chatHistoryId: chatHistoryId,
                        feedback: 'bad',
                      });
                      getRagEvaluationItem(chatHistoryId)?.feedback === 'bad'
                        ? gaClickEvent('AIアシスタント回答評価bad', {
                            event_click_state: 'OFF',
                          })
                        : gaClickEvent('AIアシスタント回答評価bad', {
                            event_click_state: 'ON',
                          });
                    }}
                    size="small"
                    color={
                      getRagEvaluationStatus(chatHistoryId) === 'bad'
                        ? 'primary'
                        : undefined
                    } //評価済みの場合は色を変更
                    disabled={isRagEvaluationLoading(chatHistoryId)} //ローディング中はボタンを無効化
                  >
                    <ThumbDownOffAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
